import React, { useRef, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useEnvironment } from 'contexts/environment'
import SmartEntityProvider, { useSmartEntity } from 'contexts/smart-entity'
import { useDocuments } from 'contexts/documents'
import { get } from 'api'
import { getSmartDocumentUrl } from 'utilities/url'
import { removeNoneCategoryFromPayload } from 'pages/documents/utilities'
import { ModalHeader } from 'modals/generic'
import Steps from 'components/steps'
import Content from './1-content'
import Details from './2-details'

const AddSmartDocument = ({
    template = null,
    concerns: initialConcerns,
    category = null,
    modal, salt
}) => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()

    const templatePickerRef = useRef()

    const { fetchFromS3 } = useEnvironment()

    const {
        entity,

        instate,
        getCustomSources,
        getPersistableContent
    } = useSmartEntity()

    const { addSmartDocument } = useDocuments()

    const [step, setStep] = useState('content')
    const [initialTemplate] = useState(template)
    const [templatePickerTriggered, setTemplatePickerTriggered] = useState(false)
    const [resolving, setResolving] = useState(false)
    const [adding, setAdding] = useState(false)
    const [defaultName, setDefaultName] = useState(null)

    const [form, setForm] = useState({
        name: null,
        description: null,
        template,
        concerns: initialConcerns?.id ?? null,
        category,
        language: null,
        legislation: null,
        notify: false
    })

    const [concerns, setConcerns] = useState(initialConcerns)

    useEffect(() => {
        if(!!templatePickerRef?.current && !templatePickerTriggered && !template) {
            setTemplatePickerTriggered(true)
            templatePickerRef.current.click()
        }
    }, [templatePickerTriggered, template])

    useEffect(() => {
        if(template) {
            resolveTemplate(template)
        }
    }, [template])

    const emptyOption = {
        id: 'empty',
        name: formatMessage({
            id: 'documents_smart_template_blank_name',
            defaultMessage: 'Blank smart document'
        })
    }

    const resolveTemplate = async template => {
        if(!template?.id || template.id === emptyOption.id) {
            setForm(form => ({
                ...form,
                ...((form.name === defaultName) ? {
                    name: null
                } : null)
            }))

            setDefaultName(null)

            instate(null)
            return
        }

        if(entity?.id === template.id) {
            return
        }

        setResolving(true)

        // TODO: Both templates fetched from the API and S3 could have a `providedBy` property (?)
        // Figure out how to deal with this
        const { ok, response } = !!template.providedBy?.id ?
            await fetchFromS3(`/templates/documents/${template.providedBy.id}/${template.id}.json`) :
            await get({ path: `/documents/templates/${template.id}` })

        setResolving(false)

        if(ok) {
            const defaultName = response.content?.[0]?.content?.[0]?.content?.[0]?.text
            if(defaultName) {
                setForm(form => ({
                    ...form,
                    ...(!form.name ? {
                        name: defaultName
                    } : null)
                }))

                setDefaultName(defaultName)
            }

            instate(response)
        }
    }

    const addDocument = async () => {
        if(adding) {
            return
        }

        // Get the initial payload
        let body = removeNoneCategoryFromPayload(form)

        body.sources = getCustomSources()
        body.language = body.language || entity?.language
        body.legislation = body.legislation || entity?.legislation
        body.category = body.category || entity?.category

        // In case the category has become General again, remove it from the payload
        body = removeNoneCategoryFromPayload(body)

        if(!!entity?.content?.length) {
            body.content = getPersistableContent()

            body.templateId = body.template?.providedBy ?
                `${body.template.providedBy.id}/${body.template.id}`
                : entity.id
        }

        setAdding(true)
        const { ok, response } = await addSmartDocument(body)
        setAdding(false)

        if(ok && response) {
            modal.dismiss()

            navigate(getSmartDocumentUrl(response), { state: { fresh: true } })
        }
    }

    const stepProps = {
        form,
        setForm,
        concerns,
        setConcerns,
        add: addDocument,
        adding,
        resolveTemplate,
        resolving,
        emptyOption,
        modal,
        salt
    }

    return (
        <>
            <ModalHeader
                heading={formatMessage({
                    id: 'documents_smart_document_action_add',
                    defaultMessage: 'Create a smart document'
                })}
                dismiss={modal.dismiss} />
            <Steps
                steps={[
                    {
                        id: 'content',
                        text: formatMessage({
                            id: 'noun_content',
                            defaultMessage: 'Content'
                        })
                    },
                    {
                        id: 'details',
                        text: formatMessage({
                            id: 'noun_details',
                            defaultMessage: 'Details'
                        })
                    }
                ]}
                at={step}
                salt={salt} />
            {(step === 'content') && (
                <Content
                    {...stepProps}
                    initialTemplate={initialTemplate}
                    templatePickerRef={templatePickerRef}
                    next={() => setStep('details')} />
            )}
            {(step === 'details') && (
                <Details
                    {...stepProps}
                    defaultName={defaultName}
                    back={() => setStep('content')} />
            )}
        </>
    )
}

export default props => (
    <SmartEntityProvider
        awaitValueResolution={false}
        context="document"
        mode="fill">
        <AddSmartDocument {...props} />
    </SmartEntityProvider>
)